import { UserId, Uuid } from '../../models/Base';
import { AlbumSummaryJson } from '../../models/RecordStore';
import { Cursor } from '../../models/Repository';
import {
  AlbumJson,
  LearningPeriod,
  Performances,
  SpaceJson,
  SpaceMembershipJson,
  SpaceMembershipTierInventoryJson,
  SpaceMembershipTierJson,
  SpaceMembershipTierSubscriptionJson,
  SpaceSummaryJson,
  SubscriptionType,
  UserRankingJson,
} from '../../models/SpaceStore';
import { PerformanceGameJson } from '../../models/SpaceStore/PerformanceGame';
import { DefaultGameDesign, WebBoosterType } from '../../models/UserStore';
import { ModifiedPick, Optional, PartialPick } from '../../models/Utils';
import {
  CursorData,
  CursorParams,
  Scope,
  ToAttachmentParams,
  WithProfilesData,
  WithProfilesParams,
} from '../utils';

import restClient, { RestRequestOptions, RestResponse } from '.';

export interface ClassroomData {
  group: SpaceJson;
  rootId: UserId;
  membership: SpaceMembershipJson | null;
  subscriptionVouchers?: unknown[];
  userMembership?: SpaceMembershipJson;
}

export interface SpacesData {
  classrooms: ClassroomData[];
}

export const getSpaces = <
  P extends {
    relatedTo?: string;
    precedent?: boolean;
    type?: string;
    userId?: UserId;
    state?: string | string[];
    role?: string | string[];
    isFavourite?: boolean;
    label?: string;
    admin?: boolean;
  } & CursorParams &
    Optional<WithProfilesParams>,
>(
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<SpacesData & CursorData & WithProfilesData<P>>> => {
  return restClient.get(`/classrooms`, params, options);
};

interface GetSpaceData {
  classroom: {
    group: SpaceJson;
    rootId: UserId;
    membership: SpaceMembershipJson | null;
  };
}

export const getSpace = <P extends Optional<WithProfilesParams>>(
  path: { classroomId: Uuid },
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<GetSpaceData & WithProfilesData<P>>> => {
  return restClient.get(`/classrooms/${path.classroomId}`, params, options);
};

type GetTierSubscriptionData = {
  classroomMembershipTierSubscriptions: SpaceMembershipTierSubscriptionJson[];
  classroomMembershipTiers: SpaceMembershipTierJson[];
  classrooms: ClassroomData[];
};

interface UpdateSpaceData {
  classroom: ClassroomData;
}
export const updateSpace = (
  path: { classroomId: Uuid },
  body: PartialPick<
    ToAttachmentParams<SpaceJson>,
    | 'i18nClassroomInfo'
    | 'state'
    | 'precedence'
    | 'icon'
    | 'banner'
    | 'region'
    | 'topics'
    | 'tags'
    | 'privacyPolicy'
    | 'gameSetPartitions'
    | 'relatedClassroomIds'
  >,
  options?: RestRequestOptions,
): Promise<RestResponse<UpdateSpaceData>> => {
  return restClient.patch(`/classrooms/${path.classroomId}`, body, options);
};

export const getSpaceMembershipTierSubscriptions = (
  params: CursorParams,
  options?: RestRequestOptions,
): Promise<RestResponse<GetTierSubscriptionData & CursorData>> => {
  return restClient.get(
    `/classrooms/classroom-membership-tier-subscriptions`,
    params,
    options,
  );
};

type GetTierInventoriesData = {
  classroomMembershipTierInventories: SpaceMembershipTierInventoryJson[];
  classroomMembershipTiers: SpaceMembershipTierJson[];
} & GetSpaceData;

export const getSpaceMembershipTierInventories = (
  params: CursorParams,
  options?: RestRequestOptions,
): Promise<RestResponse<GetTierInventoriesData & CursorData>> => {
  return restClient.get(
    `/classrooms/classroom-membership-tier-inventories`,
    params,
    options,
  );
};

export type ActivateSpaceMembershipTierInventoryBody = {
  subscriptionType: SubscriptionType;
  email?: string;
  userId?: string;
};
type ActivateSpaceMembershipTierInventoryData = {
  // classroomMembershipTierSubscription: classroomMembershipTierSubscription
  classroomMembershipTierInventory: SpaceMembershipTierInventoryJson;
};
export const activateSpaceMembershipTierInventory = (
  path: {
    classroomId: string;
    tierId: string;
  },
  body: ActivateSpaceMembershipTierInventoryBody,
  options?: RestRequestOptions,
): Promise<RestResponse<ActivateSpaceMembershipTierInventoryData>> => {
  return restClient.post(
    `/classrooms/${path.classroomId}/classroom-membership-tiers/${path.tierId}/subscription`,
    body,
    options,
  );
};

// space membership
export interface MembershipData {
  membership: SpaceMembershipJson;
  password?: string;
}
export enum SpaceMembershipOperation {
  Visit = 'visit',
  Follow = 'follow',
  Join = 'join',
  CancelJoin = 'cancelJoin',
  Accept = 'accept',
  Reject = 'reject',
  Quit = 'quit',
  Unfollow = 'unfollow',
  BlockGroup = 'blockGroup',
  UnblockGroup = 'unblockGroup',
}
export const changeUserSpaceMembership = (
  path: { classroomId: Uuid },
  body: PartialPick<
    SpaceMembershipJson,
    | 'preferredWritingSystem'
    | 'preferredSpeakingAccent'
    | 'learningLocales'
    | 'lastAccess'
  > & {
    operation: SpaceMembershipOperation;
    email?: string;
    userCode?: string;
    // invitedBy?: UserId;
  },
  options?: RestRequestOptions,
): Promise<RestResponse<MembershipData>> => {
  return restClient.post(
    `/classrooms/${path.classroomId}/membership`,
    body,
    options,
  );
};

export const seeSpace = (path: {
  classroomId: Uuid;
}): Promise<RestResponse<MembershipData>> => {
  return restClient.post(`/classrooms/${path.classroomId}/see`, {}, undefined);
};

export enum ChangeSpaceMembershipResult {
  Success = 'success',
  Error = 'error',
  InvalidOperation = 'invalid membership operation',
}

export interface LeaderboardData {
  userRanking: UserRankingJson;
  password?: string;
}
export const joinTeamForLeaderboard = (
  path: { classroomId: Uuid; leaderboardId: Uuid },
  body: {
    teamId: Uuid;
    email?: string;
  },
  options?: RestRequestOptions,
): Promise<RestResponse<LeaderboardData>> => {
  return restClient.post(
    `/classrooms/${path.classroomId}/leaderboards/${path.leaderboardId}/team`,
    body,
    options,
  );
};

export interface AlbumsData {
  gameSets: AlbumJson[];
}

export const getAlbums = <
  P extends {
    // state?: string | string[];
    partitionId?: string;
    webBoosterType?: WebBoosterType | 'both';
    // createdBy?: UserId;
    // startedBefore?: string;
    // endedAfter?: string;
    // leaderboardId?: string;
    // weeklyLeaderboard?: string;
  } & CursorParams,
>(
  path: { classroomId: string },
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<AlbumsData & CursorData>> => {
  return restClient.get(
    `/classrooms/${path.classroomId}/game-sets`,
    params,
    options,
  );
};

export interface RecentAlbumsData {
  gameSets: AlbumJson[];
  classrooms: ClassroomData[];
}

export const getRecentAlbums = <
  P extends { classroomId?: string } & CursorParams &
    Optional<WithProfilesParams>,
>(
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<
  RestResponse<RecentAlbumsData & CursorData & WithProfilesData<P>>
> => {
  return restClient.get(`/classrooms/game-sets/recent`, params, options);
};

interface AlbumData {
  gameSet: AlbumJson;
}
export const getAlbum = (
  path: { classroomId: Uuid; gameSetId: Uuid },
  params: Scope = {},
  options?: RestRequestOptions,
): Promise<RestResponse<AlbumData>> => {
  return restClient.get(
    `/classrooms/${path.classroomId}/game-sets/${path.gameSetId}`,
    params,
    options,
  );
};

interface CreateAlbumData {
  gameSet: AlbumJson;
  // post, channel, data
}

export const createAlbum = (
  path: { classroomId: string },
  body: { defaultGameDesign?: DefaultGameDesign } & ModifiedPick<
    AlbumJson,
    'i18nGameSetInfo',
    | 'startedAt'
    | 'endedAt'
    | 'state'
    | 'partitionId'
    | 'index'
    | 'icons'
    | 'tags'
    | 'audience'
    | 'viewOnlyAudience'
    | 'isCopyable'
    | 'content'
    | 'challenge'
    | 'revision'
    | 'leaderboardId'
    | 'tournament'
  >,
  options?: RestRequestOptions,
): Promise<RestResponse<CreateAlbumData>> => {
  return restClient.post(
    `/classrooms/${path.classroomId}/game-sets/`,
    body,
    options,
  );
};

export const updateAlbum = (
  path: { classroomId: string; gameSetId: string },
  body: PartialPick<
    AlbumJson,
    | 'startedAt'
    | 'endedAt'
    | 'i18nGameSetInfo'
    | 'state'
    | 'partitionId'
    | 'index'
    | 'icons'
    | 'tags'
    | 'audience'
    | 'viewOnlyAudience'
    | 'isCopyable'
    | 'content'
    | 'challenge'
    | 'revision'
    | 'leaderboardId'
    | 'tournament'
  >,
  options?: RestRequestOptions,
): Promise<RestResponse<AlbumData>> => {
  return restClient.patch(
    `/classrooms/${path.classroomId}/game-sets/${path.gameSetId}`,
    body,
    options,
  );
};

export const deleteAlbum = (
  path: { classroomId: string; gameSetId: string },
  options?: RestRequestOptions,
): Promise<RestResponse<AlbumData>> => {
  return restClient.delete(
    `/classrooms/${path.classroomId}/game-sets/${path.gameSetId}`,
    {},
    options,
  );
};

interface AlbumSummariesData {
  gameSetSummaries: AlbumSummaryJson[];
}
export const getAlbumSummaries = <
  P extends {
    gameSetIds?: Uuid[];
    classroomId?: Uuid;
  } & CursorParams,
>(
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<AlbumSummariesData & CursorData>> => {
  return restClient.get(`/classrooms/game-set-summaries`, params, options);
};

interface UpdateAlbumSummaryData {
  gameSetSummary: AlbumSummaryJson;
  classroomSummary: SpaceSummaryJson;
}
export const updateAlbumSummary = (
  path: { classroomId: Uuid; gameSetId: Uuid },
  options?: RestRequestOptions,
): Promise<RestResponse<UpdateAlbumSummaryData>> => {
  return restClient.post(
    `/classrooms/${path.classroomId}/game-sets/${path.gameSetId}/game-set-summaries`,
    {},
    options,
  );
};

interface AlbumSummaryData {
  gameSetSummary: AlbumSummaryJson;
}

export const updateAlbumSummaryPath = (
  path: { classroomId: Uuid; gameSetId: Uuid },
  body: { showGameForChallengeOnly?: boolean },
  options?: RestRequestOptions,
): Promise<RestResponse<AlbumSummaryData>> => {
  return restClient.patch(
    `/classrooms/${path.classroomId}/game-sets/${path.gameSetId}/path`,
    body,
    options,
  );
};

export const moveInAlbumSummaryPath = (
  path: { classroomId: Uuid; gameSetId: Uuid },
  body: { moveTo: number },
  options?: RestRequestOptions,
): Promise<RestResponse<AlbumSummaryData>> => {
  return restClient.post(
    `/classrooms/${path.classroomId}/game-sets/${path.gameSetId}/path/move`,
    body,
    options,
  );
};

interface ViewStartVideoData {
  gameSetSummary: AlbumSummaryJson;
  gameSet: AlbumJson;
}
export const viewStartVideo = (
  path: { classroomId: Uuid; gameSetId: Uuid },
  options?: RestRequestOptions,
): Promise<RestResponse<ViewStartVideoData>> => {
  return restClient.post(
    `/classrooms/${path.classroomId}/game-sets/${path.gameSetId}/path/start-video-view`,
    {},
    options,
  );
};

export interface SpaceSummaryData {
  classroomSummaries: SpaceSummaryJson[];
}

export const getSpaceSummaries = <
  P extends {
    classroomIds: Uuid[];
  },
>(
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<SpaceSummaryData>> => {
  return restClient.get(`/classrooms/classroom-summaries`, params, options);
};

interface UpdateSpaceSummaryData {
  classroomSummary: SpaceSummaryJson;
}
export const updateSpaceSummary = (
  path: { classroomId: Uuid },
  body: { currentLearningPeriod?: LearningPeriod<number> } = {},
  options?: RestRequestOptions,
): Promise<RestResponse<UpdateSpaceSummaryData>> => {
  return restClient.post(
    `/classrooms/${path.classroomId}/classroom-summaries`,
    body,
    options,
  );
};

type SearchSpacesData =
  //  SpaceFoldersData &
  SpacesData &
    Partial<{
      classroomFolderCursor: string;
      classroomCursor: string;
    }>;
export const searchSpaces = <
  P extends {
    key?: string;
    isCertified?: boolean;
    minPrecedence?: number;
    createdBy?: UserId[];
    type?: string[];
    state?: string;
    region?: string[];
    level?: string[];
    label?: string;
    topics?: string[];
    classroomLimit?: number;
    classroomCursor?: Cursor;
    classroomFolderLimit?: number;
    classroomFolderCursor?: Cursor;
  } & Optional<WithProfilesParams>,
>(
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<
  RestResponse<
    SearchSpacesData &
      // & SearchHistoryData
      WithProfilesData<P>
  >
> => {
  return restClient.get('/classrooms/search', params, options);
};

export interface SpacePerformanceReportSummary {
  completedGameCount: number;
  scoreTotal: number;
  totalDurationInMs: number;
  createdAt: number | undefined;
  lastModifiedAt: number;
  performances: Performances;
}

export interface SpacePerformanceReportData {
  performanceReport: SpacePerformanceReportSummary;
}

export const getSpacePerformanceReport = <
  P extends {
    webBoosterType?: WebBoosterType | 'both';
    partitionId?: string;
    from?: string;
    to?: string;
  },
>(
  path: { classroomId: Uuid },
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<SpacePerformanceReportData>> => {
  return restClient.get(
    `/classrooms/${path.classroomId}/performance-report`,
    params,
    options,
  );
};

export interface SpacePerformanceReportGameData {
  games: PerformanceGameJson[];
}

export const getSpacePerformanceReportGame = <
  P extends {
    webBoosterType?: WebBoosterType | 'both';
    partitionId?: string;
    from?: string;
    to?: string;
  } & CursorParams,
>(
  path: { classroomId: Uuid },
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<SpacePerformanceReportGameData & CursorData>> => {
  return restClient.get(
    `/classrooms/${path.classroomId}/performance-report/games`,
    params,
    options,
  );
};
